import Context from "./storage/space/Context.js";
import Dexie from "dexie";
import Error from "./utils/Error.js";
import global from "./global.js";
import Mutex from "./utils/Mutex.js";
import Room from "@/prototype/Room.js";
import Space from "./storage/Space.js";

import AlertArea from "./ui/AlertArea.js";
import LoadingArea from "./ui/LoadingArea.js";
import ToastArea from "./ui/ToastArea.js";
import {LOCAL_SPACE_NUMBER, LOCAL_DESKTOP_NUMBER} from "./storage/constants.js";

const log = global.log.getLogger("f_mtac.storage");
log.setLevel(log.levels.WARN);

// Dexie.debug = true;

let Storage = function () {
    var spaces = Array.from(Array(LOCAL_SPACE_NUMBER), () => null);
    var space_lock = new Mutex();
    var space = null;
    var room_to_recover = null; // room to recover after a server restart

    this.space_data = new Proxy(Object(), {
        get: (_, space_index) => Space.data_store({space_index})
    });

    this.desktop_data = Array.from(
        Array(LOCAL_SPACE_NUMBER),
        (_, space_index) =>
            new Proxy(Object(), {
                get: (_, desktop_index) => Context.data_store({space_index, desktop_index})
            })
    );

    Object.defineProperty(this, "space", {
        set: () => {},
        get: () => spaces
    });

    const subscribe = function (space) {
        global.server.subscribe.on("object", space.on_object_server_update);
        global.server.subscribe.on("configuration", space.on_configuration_server_update);
        global.server.subscribe.on("message", space.on_message_server_update);
        global.server.subscribe.on("room", space.on_room_server_update);
    };

    const unsubscribe = function () {
        global.server.subscribe.on("object", null);
        global.server.subscribe.on("configuration", null);
        global.server.subscribe.on("message", null);
        global.server.subscribe.on("room", null);
    };

    const change_space = async (old_space, new_space) => {
        var release = await space_lock.acquire();
        if (old_space !== null) {
            unsubscribe();
        }
        if (new_space !== null) {
            subscribe(new_space);
        }
        if (old_space !== new_space) {
            space = new_space;
            global.event_bus.publish("storage.space.changed", {old_space, new_space});
            global.configuration.app.last_space = new_space?.index;
            global.configuration.commit_store("app");
        }
        release();
    };

    const get_space_by_index = (space_index) => {
        if (space_index === undefined) {
            if (space === null) {
                throw new Error.build.nodata({
                    message: "no selected space"
                });
            }
            space_index = space.index;
        } else {
            if (typeof space_index !== "number") {
                throw new Error.build.invalid({
                    message: "wrong space_index type, shall be a number"
                });
            }
        }
        if (space_index < 0 || space_index >= LOCAL_SPACE_NUMBER) {
            throw new Error.build.invalid({
                message: `space index shall be between 0 and ${LOCAL_SPACE_NUMBER}`
            });
        }
        return this.space[space_index];
    };

    Object.defineProperty(this, "selected_context", {
        set: () => {},
        get: () => (space === null ? null : space.selected_context)
    });

    Object.defineProperty(this, "selected_space", {
        set: () => {},
        get: () => space
    });

    // @brief handler on the notification "network.online"
    global.event_bus.subscribe("network.online", async () => {
        log.debug("Storage -> on network.online in");
        if (space === null) {
            log.debug("Storage -> on network.online space is null");
            return;
        }
        if (space.room === null) {
            log.debug("Storage -> on network.online, space.room is null");
            return;
        }

        // check if the room is still on the server
        let status = await space.check_room();
        log.debug("Storage -> on network.online, check room status = ", {status});
        if (status == 404) {
            // room not found means that the server has restarted
            // save the current room
            room_to_recover = new Room(`${space.room.space}${global.storage.selected_context.index}${space.room.secret}`);
            status = await this.recover_room();
            log.debug("Storage -> on network.online recover_room status =", {status});
            if (status == Error.status.conflict) {
                // if conflict, retry reconnection in few millisec
                log.debug("Storage -> on network.online schedule reconnect_to_room");
                setTimeout(() => {
                    this.reconnect_to_room();
                }, 200);
            }
        } else {
            log.debug("Storage -> on network.online not 404");

            // AGPOC-3539 : check if background_image ( = map_media_id ) already exists in server
            // if at least one background_image ( = map_media_id ) exists on server and is different of current context background_iamge => do not push!
            // the synchronization question will be asked to user (conflict raised) during context_index pull operation when re-synchronizing will be needed
            log.debug("room ", space.room);
            if (space.room !== null) {
                try {
                    let response = await global.server.mtac_room_desktop_list(space.room);
                    var {name, list} = {name: response.data.name, list: response.data.list};
                } catch (error) {
                    switch (error.status) {
                        case Error.status.netdown:
                        case Error.status.hostdown:
                            log.debug("Storage -> on network.online OUT");
                            return;
                        case Error.status.auth:
                            log.debug("Storage -> on network.online error authentication, uncomment ?????");
                            break;
                        default:
                            throw error;
                    }
                }
                if (name !== undefined) {
                    log.debug("name ", name);
                }
                log.debug("list ", list);
                if (!list) {
                    return;
                }
                if (list.length < LOCAL_DESKTOP_NUMBER) {
                    throw "list is not providing enough desktops";
                }
                for (let desktop_index = 0; desktop_index < LOCAL_DESKTOP_NUMBER; desktop_index++) {
                    if (list[desktop_index] === null) {
                        continue;
                    }
                    let {background_image} = list[desktop_index]; // background_image stored in server = map media id
                    let map_media_id = background_image;
                    if (map_media_id === null) {
                        continue;
                    }
                    let context = space.context[desktop_index];
                    if (context === null) {
                        continue;
                    }
                    let context_map_media = await context.map_media_id;
                    log.debug("context_map_media ", context_map_media);
                    if (context_map_media !== null && `media-${map_media_id}` !== context_map_media && map_media_id !== "000102030405060708090a0b0c0d0e0f") {
                        log.warn("map media (former background image) is different, do not push it", map_media_id, "!=", context_map_media);
                        return;
                    }
                }
            }
            log.debug("Storage -> on network.online no conflict space can be pushed");
            await space.push();
            log.debug("Storage -> on network.online before update");
            await space.update();
            log.debug("Storage -> on network.online before pull");
            await space.pull();
            log.debug("Storage -> on network.online out");
        }
    });

    // @brief Reconnect to the room saved in "room_to_recover".
    //  If an error 409 is raised, the server is not ready, retry few seconds later
    this.reconnect_to_room = async () => {
        log.debug("Storage.reconnect_to_room", room_to_recover);

        //var new_room = new Room(`${space.room.space}${global.storage.selected_context.index}${space.room.secret}`);
        try {
            log.debug("Storage.reconnect_to_room subscribe.disconnect");
            await global.server.subscribe.disconnect();
            await global.storage.unshare();
            await global.server.mtac_room_connect(room_to_recover);
            await this.fetch(room_to_recover);
            await global.server.subscribe.connect();
            log.debug("Storage.reconnect_to_room done");
        } catch (error) {
            log.debug("Storage.reconnect_to_room catched error: ", error.status);
            if (error.status == Error.status.conflict) {
                // if still conflict, retry reconnection in few millisec
                setTimeout(() => {
                    this.reconnect_to_room();
                }, 200);
            }
            return error.status;
        }
        log.debug("Storage.reconnect_to_room out");
    };

    // @brief This should be called after a restart of the server
    // Try to share the room  as it was before the restart
    // there may be concurency between several devices that try to recover
    // the room at the same time.
    // @return nothing if success
    //         Error.status.conflict if conflict on recovering
    this.recover_room = async () => {
        log.debug("Storage.recover_room in", space.room);

        // remove all Agnet resources otherwise they are duplicated
        await this.selected_space.delete_agnet_resources_in_all_context();

        var new_room = new Room(`${space.room.space}${global.storage.selected_context.index}${space.room.secret}`);
        try {
            // creation with transaction start
            await global.server.mtac_room_create({
                room_id: `${space.room.space}0${space.room.secret}`,
                transaction: "start",
                org_name: global.configuration.commissioning.organization
            });
            await this.unshare();
            log.debug(`Storage.recover_room org = ${global.configuration.commissioning.organization}`);
            await global.server.mtac_room_connect({
                space: new_room.space,
                desktop: new_room.desktop,
                secret: new_room.secret,
                org_name: global.configuration.commissioning.organization
            });
            await this.share(new_room);
            await global.server.subscribe.connect();

            // transaction = "end"
            await global.server.mtac_room_creation_end({
                room_id: `${space.room.space}0${space.room.secret}`
            });
        } catch (error) {
            log.debug("Storage.recover_room received exception: ", error);
            return error.status;
        }
        log.debug("Storage.recover_room out");
    };

    this.ignore_transaction = function (operation) {
        if (Dexie.currentTransaction !== null) {
            Dexie.ignoreTransaction(operation);
        } else {
            operation();
        }
    };

    this.isStoragePersisted = async () => {
        if (!navigator.storage || !navigator.storage.persist) {
            log.error("this navigator doesn't support storage persistance or your session is not secure");
            return true;
        }
        return navigator.storage.persisted();
    };

    this.persist = async () => {
        if (await this.isStoragePersisted()) {
            return;
        }
        await navigator.storage.persist();
        if (!(await this.isStoragePersisted())) {
            log.warn("warning: unable to enable the storage persistance");
        }
    };

    this.open_space = async function ({space_index}) {
        log.debug("Open space:", space_index);
        let new_space = get_space_by_index(space_index);
        if (new_space !== null) {
            return new_space;
        }
        new_space = new Space(space_index);
        await new_space.open();
        this.space[space_index] = new_space;
        return new_space;
    };

    this.database_exists = async (name) => {
        if (window.indexedDB.databases === undefined) {
            log.warn("indexedDB.databases list not available falling back to a slower Dexie.exists");
            return await Dexie.exists(name);
        }
        return (await window.indexedDB.databases()).map((db) => db.name).includes(name);
    };

    this.space_exists = async function ({space_index}) {
        for (let desktop_index = 0; desktop_index < LOCAL_DESKTOP_NUMBER; desktop_index++) {
            if (await this.database_exists(Context.database_name({space_index, desktop_index}))) {
                return true;
            }
        }
        return false;
    };

    this.open = async function ({space_index, desktop_index}) {
        var old_space = space;
        var new_space = await this.open_space({space_index});
        if (old_space !== new_space && old_space !== null) {
            await old_space.close_space();
        }
        await change_space(old_space, new_space);
        desktop_index ??= global.storage.selected_space.last_context || 0;
        let {old_context, new_context} = await space.open_context({desktop_index});
        if (old_space !== null && old_space !== new_space) {
            old_context = old_space.selected_context;
        }
        return {old_context, new_context};
    };

    this.duplicate = async function ({space_index, desktop_index, new_space_index, new_desktop_index}) {
        if (new_desktop_index === undefined) {
            return;
        }
        let old_space = get_space_by_index(space_index);
        if (old_space === null) {
            return;
        }
        let old_context = old_space.context[desktop_index];
        if (old_context === null) {
            return;
        }
        let new_space = get_space_by_index(new_space_index);
        if (new_space === null) {
            new_space = new Space(new_space_index);
            await new_space.open();
            this.space[new_space_index] = new_space;
        }
        await old_space.duplicate(new_space.index);

        await new_space.delete_context({desktop_index: new_desktop_index});
        await old_context.duplicate(new_space.index, new_desktop_index);
        await new_space.create_context({desktop_index: new_desktop_index});
        if (old_space === new_space && new_space.room !== null) {
            let new_room = new_space.room.duplicate();
            new_room.desktop_index = new_desktop_index;
            await space.context[new_desktop_index].share(new_room);
            await space.context[new_desktop_index].push();
        }
    };

    this.duplicate_channel = async function ({space_index, channel_index, new_space_index, new_channel_index}) {
        if (new_channel_index === undefined) {
            return;
        }
        let old_space = get_space_by_index(space_index);
        if (old_space === null) {
            return;
        }
        let old_channel = old_space.channel[channel_index];
        if (old_channel === null) {
            return;
        }
        let new_space = get_space_by_index(new_space_index);
        if (new_space === null) {
            new_space = new Space(new_space_index);
            await new_space.open();
            this.space[new_space_index] = new_space;
        }
        await old_space.duplicate(new_space.index);

        await new_space.delete_all_channels();
        await old_channel.duplicate(new_space.index, new_channel_index);
        await new_space.create_channel({channel_index: new_channel_index});
        if (old_space === new_space && new_space.room !== null) {
            let new_room = new_space.room.duplicate();
            new_room.channel_index = new_channel_index;
            await space.channel[new_channel_index].init(new_room);
            await space.channel[new_channel_index].push();
        }
    };

    this.close = async function ({space_index, desktop_index}) {
        let old_space = get_space_by_index(space_index);
        if (old_space === null) {
            return;
        }
        space.close_context({desktop_index});
    };

    this.delete_space = async function ({space_index}) {
        let old_space = get_space_by_index(space_index);
        if (old_space === null) {
            return;
        }
        let old_space_index = old_space.index;
        if (space === old_space) {
            await change_space(old_space, null);
        }
        log.debug("Delete space:", space_index);
        await old_space.delete();
        spaces[old_space_index] = null;
    };

    this.delete = async function ({space_index, desktop_index}) {
        let old_space = get_space_by_index(space_index);
        if (old_space === null) {
            return;
        }
        await old_space.delete_context({desktop_index});
    };

    this.fetch = async function (room, desktop_index = 0) {
        log.debug("storage.fetch in");
        if (space === null) {
            log.error("no space selected");
            return;
        }
        var old_space = space;
        var old_space_index = old_space.index;
        var new_space = null;
        await this.delete_space({space_index: old_space_index});
        new_space = await this.open_space({space_index: old_space_index});
        await new_space.share(room);
        let first_context = null;
        if (desktop_index) {
            await this.open({space_index: new_space.index, desktop_index});
        } else {
            for (let current_context of new_space.context) {
                if (current_context === null) {
                    continue;
                }
                if (first_context === null) {
                    first_context = current_context;
                }
                await this.open({space_index: new_space.index, desktop_index: current_context.index});
                break;
            }
            if (first_context === null) {
                await this.open({space_index: new_space.index, desktop_index: 0});
                let current_room = room.duplicate();
                current_room.desktop_index = 0;
                await new_space.context[0].share(current_room);
            }
        }
        global.event_bus.publish("storage.shared", {room});
        log.debug("storage.fetch out");
    };

    this.shared = function () {
        let is_shared = space !== null && space.room !== null;
        //log.debug("storage.shared", {is_shared});
        return is_shared;
    };

    this.share = async function (room) {
        log.debug("storage share in", {room, space});
        if (space === null) {
            return;
        }
        const space_name = space.name || global.internationalization.text("global.Without-name"); // if falsy
        try {
            await global.server.mtac_room_update({space: room.space, name: space_name});
        } catch (error) {
            log.debug("mtac_room_update error", error);
            switch (error.status) {
                case Error.status.netdown:
                case Error.status.hostdown:
                    break;
                default:
                    log.error("error while updating space name", error);
                    break;
            }
        }
        await space.share(room);
        global.event_bus.publish("storage.shared", {room});
        log.debug("storage share out");
    };

    this.unshare = async function () {
        if (space === null) {
            return;
        }
        await space.unshare();
        global.event_bus.publish("storage.unshared", {room: null});
    };

    this.get_space_name = async function ({space_index}) {
        let new_space = get_space_by_index(space_index);
        return new_space.name;
    };
};

global.app.bootstrap.register_module({
    name: "storage",
    deps: ["app"],
    init: async () => {
        global.storage = new Storage();
    }
});

global.app.bootstrap.register_module({
    name: "context_select",
    deps: ["storage", "check_device_registration", "ui_desktop_settings_area_tools", "ui_ui_settings_area_tools", "ui_map_settings_area_tools", "ui_background_image_settings_area_tools", "ui_grid_settings_area_tools", "ui_resources_area_tools"],
    init: async () => {
        await global.storage.open({space_index: global.configuration.app.last_space ?? 0});
        $("#splash").hide();

        let room = new Room(global.storage.selected_space.room_short_id);
        if (!room.short_id) {
            return;
        }
        // Try to connect and fetch back previous room
        try {
            await global.server.mtac_room_connect({
                space: room.space,
                desktop: 0,
                secret: room.secret,
                org_name: global.configuration.commissioning.organization
            });
        } catch (error) {
            log.info(`Room ${room.space}${room.secret} not found on the server`);
            global.storage.selected_space.room_short_id = "";
            await ToastArea.interact("warn", global.internationalization.text("collaboration.Unknown-pin-code"));
            return;
        }
        LoadingArea.interact({tip: global.internationalization.text("collaboration.Synchronization-in-progress")});
        try {
            await global.storage.fetch(room, global.storage.selected_space.last_context); // GET config, object, media, message, users
            await global.server.subscribe.connect(); // Subscribe to websocket
            LoadingArea.close();
        } catch (error) {
            LoadingArea.close();
            await AlertArea.interact({
                type: "danger",
                tip1: global.internationalization.text("collaboration.Sharing-not-possible-unreachable-server")
            });
        }
    }
});

export default Storage;
